import React, { useMemo } from 'react';
import { InfoTooltip } from '../common/InfoTooltip';

interface TraitAnalysis {
  collaboration_score?: number;
  collaboration_category?: string;
  leadership_potential_score?: number;
  leadership_potential_category?: string;
  decisive_score?: number;
  decisive_category?: string;
  empathetic_score?: number;
  empathetic_category?: string;
  direct_score?: number;
  direct_category?: string;
}

interface Analysis {
  id: string;
  date?: string;
  trait_analysis?: TraitAnalysis;
}

interface DominantTraitsProps {
  analyses: Analysis[];
}

interface TraitStats {
  name: string;
  description: string;
  min: number;
  max: number;
  average: number;
  category: string;
  trend: 'improving' | 'stable' | 'declining';
  color: {
    line: string;
    dot: string;
    badge: string;
  };
}

// Trait configuration
const TRAIT_CONFIG = {
  collaboration: {
    name: 'Collaboration',
    description: 'Works effectively with others and values team input',
  },
  leadership_potential: {
    name: 'Leadership Potential',
    description: 'Shows ability to guide and inspire teams',
  },
  decisive: {
    name: 'Decisiveness',
    description: 'Makes clear, timely decisions with confidence',
  },
  empathetic: {
    name: 'Empathy',
    description: 'Demonstrates understanding and consideration for others',
  },
  direct: {
    name: 'Directness',
    description: 'Communicates clearly and straightforwardly',
  },
};

// Category colors
const CATEGORY_COLORS = {
  strong: {
    line: 'bg-green-500',
    dot: 'bg-green-700',
    badge: 'bg-green-100 text-green-800',
  },
  neutral: {
    line: 'bg-blue-500',
    dot: 'bg-blue-700',
    badge: 'bg-blue-100 text-blue-800',
  },
  weak: {
    line: 'bg-gray-400',
    dot: 'bg-gray-600',
    badge: 'bg-gray-100 text-gray-600',
  },
};

// Helper function to calculate linear regression slope
const calculateLinearRegressionSlope = (values: number[]): number => {
  const n = values.length;
  if (n < 2) return 0;

  // x values are just indices (0, 1, 2, 3, ...)
  const xValues = Array.from({ length: n }, (_, i) => i);
  
  // Calculate means
  const xMean = xValues.reduce((sum, x) => sum + x, 0) / n;
  const yMean = values.reduce((sum, y) => sum + y, 0) / n;
  
  // Calculate slope using least squares formula
  let numerator = 0;
  let denominator = 0;
  
  for (let i = 0; i < n; i++) {
    numerator += (xValues[i] - xMean) * (values[i] - yMean);
    denominator += Math.pow(xValues[i] - xMean, 2);
  }
  
  return denominator === 0 ? 0 : numerator / denominator;
};

// Helper function to get trend icon (based on temporal analysis)
const getTrendIcon = (trend: 'improving' | 'stable' | 'declining'): string => {
  switch (trend) {
    case 'improving':
      return 'trending_up';
    case 'stable':
      return 'trending_flat';
    case 'declining':
      return 'trending_down';
    default:
      return 'trending_flat';
  }
};

export const DominantTraits: React.FC<DominantTraitsProps> = ({ analyses }) => {
  const traitStats = useMemo(() => {
    if (!analyses || analyses.length === 0) {
      return [];
    }

    // Sort analyses by date (oldest to newest)
    const sortedAnalyses = [...analyses].sort((a, b) => {
      const dateA = a.date ? new Date(a.date).getTime() : 0;
      const dateB = b.date ? new Date(b.date).getTime() : 0;
      return dateA - dateB;
    });

    const stats: TraitStats[] = [];

    // Process each trait type
    Object.entries(TRAIT_CONFIG).forEach(([traitKey, config]) => {
      const scoreKey = `${traitKey}_score` as keyof TraitAnalysis;
      const categoryKey = `${traitKey}_category` as keyof TraitAnalysis;

      // Collect all scores and categories for this trait (in chronological order)
      const scores: number[] = [];
      const categories: string[] = [];
      
      sortedAnalyses.forEach((analysis) => {
        const score = analysis.trait_analysis?.[scoreKey];
        const cat = analysis.trait_analysis?.[categoryKey];
        
        if (typeof score === 'number') {
          scores.push(score);
        }
        if (typeof cat === 'string') {
          categories.push(cat);
        }
      });

      // Skip if no data for this trait
      if (scores.length === 0) {
        return;
      }

      // Calculate statistics
      const min = Math.min(...scores);
      const max = Math.max(...scores);
      const average = scores.reduce((sum, s) => sum + s, 0) / scores.length;

      // Determine category from most common category (not average)
      const categoryCounts: Record<string, number> = {};
      categories.forEach((cat) => {
        categoryCounts[cat] = (categoryCounts[cat] || 0) + 1;
      });
      
      // Find most common category
      let mostCommonCategory = 'neutral';
      let maxCount = 0;
      Object.entries(categoryCounts).forEach(([cat, count]) => {
        if (count > maxCount) {
          maxCount = count;
          mostCommonCategory = cat;
        }
      });
      
      const category = mostCommonCategory;
      const colors = CATEGORY_COLORS[category as keyof typeof CATEGORY_COLORS] || CATEGORY_COLORS.neutral;

      // Calculate temporal trend using linear regression
      let trend: 'improving' | 'stable' | 'declining' = 'stable';
      
      if (scores.length >= 3) {
        // Need at least 3 data points for meaningful trend analysis
        const slope = calculateLinearRegressionSlope(scores);
        
        // Normalize slope relative to the average score to get percentage change per analysis
        const avgScore = average / 100; // Convert back to 0-1 scale
        const normalizedSlope = avgScore > 0 ? (slope / avgScore) * 100 : 0;
        
        // Threshold: if slope indicates >0.5% change per analysis, it's significant
        // For 10 analyses, this would mean ~5% total change
        if (normalizedSlope > 0.5) {
          trend = 'improving';
        } else if (normalizedSlope < -0.5) {
          trend = 'declining';
        } else {
          trend = 'stable';
        }
      }

      stats.push({
        name: config.name,
        description: config.description,
        min: min * 100,
        max: max * 100,
        average: average * 100,
        category,
        trend,
        color: colors,
      });
    });

    // Sort by average score descending
    return stats.sort((a, b) => b.average - a.average);
  }, [analyses]);

  // If no data available
  if (!analyses || analyses.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Dominant Communication Traits</h3>
        <div className="text-center py-8 text-gray-500">
          <svg className="mx-auto h-12 w-12 text-gray-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <p className="text-sm">No trait analysis data available yet</p>
          <p className="text-xs text-gray-400 mt-1">Analyse more communications to see your traits</p>
        </div>
      </div>
    );
  }

  if (traitStats.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Dominant Communication Traits</h3>
        <div className="text-center py-8 text-gray-500">
          <p className="text-sm">Insufficient trait data in analyses</p>
          <p className="text-xs text-gray-400 mt-1">Trait analysis data is being generated for your communications</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
      <div className="flex items-start justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Dominant Communication Traits</h3>
        <InfoTooltip
          title="Dominant Communication Traits"
          content={[
            "Shows your top 5 communication traits based on analysis of your messages: Collaboration, Leadership Potential, Decisiveness, Empathy, and Directness.",
            "The colored bar represents your range (min to max scores), with a dot showing your average. This helps you see both consistency and variability in each trait.",
            "The trend arrow indicates whether you're improving (↗), stable (→), or declining (↘) over time, calculated using linear regression across all your communications.",
            "Categories (Strong/Neutral/Weak) are determined by the most common assessment across all your analyses."
          ]}
          position="left"
        />
      </div>

      <div className="space-y-6">
        {traitStats.map((trait, index) => (
          <div key={index} className="relative">
            {/* Header */}
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="font-medium text-gray-900">{trait.name}</h4>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${trait.color.badge}`}>
                    {trait.category.charAt(0).toUpperCase() + trait.category.slice(1)}
                  </span>
                </div>
                <p className="text-sm text-gray-500">{trait.description}</p>
              </div>
              <div className="flex items-center gap-2 ml-4">
                <span 
                  className={`material-symbols-outlined text-lg ${
                    trait.trend === 'improving' ? 'text-green-600' :
                    trait.trend === 'declining' ? 'text-red-600' :
                    'text-gray-500'
                  }`}
                  title={`Trend: ${trait.trend}`}
                >
                  {getTrendIcon(trait.trend)}
                </span>
                <span className="text-lg font-semibold text-gray-900">
                  {Math.round(trait.average)}%
                </span>
              </div>
            </div>

            {/* Visualization Bar */}
            <div className="relative h-2 bg-gray-200 rounded-full overflow-visible">
              {/* Range line - only show if min !== max */}
              {trait.min !== trait.max && (
                <div
                  className={`absolute h-1 ${trait.color.line} top-0.5 rounded-full`}
                  style={{
                    left: `${trait.min}%`,
                    width: `${trait.max - trait.min}%`,
                  }}
                />
              )}

              {/* Average dot */}
              <div
                className={`absolute w-3 h-3 ${trait.color.dot} rounded-full border-2 border-white -top-0.5 shadow-sm`}
                style={{
                  left: `calc(${trait.average}% - 6px)`,
                }}
                title={`Average: ${Math.round(trait.average)}%`}
              />
            </div>

            {/* Labels */}
            <div className="flex justify-between mt-1 text-xs text-gray-500">
              <span>Min: {Math.round(trait.min)}%</span>
              <span className="font-medium text-gray-700">Avg: {Math.round(trait.average)}%</span>
              <span>Max: {Math.round(trait.max)}%</span>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 pt-4 border-t border-gray-200">
        <p className="text-xs text-gray-500">
          Based on {analyses.length} communication{analyses.length !== 1 ? 's' : ''} analyzed. 
          Traits show your range (min-max) and average score across all analyses.
        </p>
      </div>
    </div>
  );
};
